import { useState, useEffect, useCallback } from 'react';
import './App.css';
import NavBar from './NavBar';
import Tokenomics from './Tokenomics';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ethers } from "ethers";
import mieroNFT from "./MIERO.json";

// Update contract address logic
const mieroNFTAddress = process.env.NODE_ENV === 'development' 
  ? process.env.REACT_APP_SEPOLIA_Contract 
  : process.env.REACT_APP_MAINNET_Contract;

// Add network constants
const CHAIN_ID = {
  MAINNET: 1n,    // 0x1
  SEPOLIA: 11155111n  // 0xaa36a7
};

function App() {
  // Wallet connection states
  //console.log(`Logging contract address: ${mieroNFTAddress}`);
  const [accounts, setAccounts] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isAppConnected, setIsAppConnected] = useState(false);

  // Minting states
  const [mintAmount, setMintAmount] = useState(1);
  const [message, updateMessage] = useState("");
  const [isSoldOut, setIsSoldOut] = useState(false);
  const [isInsuffcientGas, setInsuffcientGas] = useState(false);
  const [isInMetaMaskHands, setInMetaMaskHands] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Add new state for transaction status
  const [isTransactionPending, setIsTransactionPending] = useState(false);

  // Add new state for minting status
  const [isMinting, setIsMinting] = useState(false);

  // Format wallet address
  const formatWalletAddress = useCallback((address) => {
    if (!address) return '';
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  }, []);

  // Reset state
  const resetState = useCallback(() => {
    setAccounts([]);
    setIsAppConnected(false);
    localStorage.setItem('walletConnected', 'false');
  }, []);

  // Disconnect wallet
  const disconnectWallet = useCallback(() => {
    console.log("Disconnecting wallet...");
    resetState();
    console.log("Wallet disconnected");
  }, [resetState]);

  // Connect wallet
  const connectWallet = useCallback(async () => {
    console.log("Attempting to connect wallet...");
    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts"
      });
      console.log("Accounts received:", accounts);

      if (accounts.length > 0) {
        setAccounts(accounts);
        setIsAppConnected(true);
        localStorage.setItem('walletConnected', 'true');
        console.log("Wallet connected successfully");
      }
    } catch (error) {
      console.error("Connection error:", error);
      resetState();
    }
  }, [resetState]);

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return /android|iPad|iPhone|iPod/i.test(userAgent);
    };
    setIsMobile(checkIfMobile());
  }, []);

  // Handle account changes
  const handleAccountsChanged = useCallback((newAccounts) => {
   // console.log("Accounts changed:", newAccounts);
    if (newAccounts.length > 0) {
      setAccounts(newAccounts);
      setIsAppConnected(true);
      localStorage.setItem('walletConnected', 'true');
    } else {
      resetState();
    }
  }, [resetState]);

  // Check initial connection and set up listeners
  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          const currentAccounts = await window.ethereum.request({ method: 'eth_accounts' });
        //  console.log("Checking current accounts:", currentAccounts);
          handleAccountsChanged(currentAccounts);
        } catch (error) {
          console.error("Error checking accounts:", error);
          resetState();
        }
      }
    };

    checkConnection();

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('disconnect', disconnectWallet);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('disconnect', disconnectWallet);
      };
    }
  }, [handleAccountsChanged, disconnectWallet, resetState]);

  // Monitor state changes
  useEffect(() => {
  //  console.log("Connection state changed:", { accounts, isAppConnected });
  }, [accounts, isAppConnected]);

  // Minting functionality
  async function handleMint() {
    if (typeof window.ethereum !== 'undefined') {
        try {
            setIsMinting(true);
            updateMessage("Initializing minting process...");

            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();
            const contract = new ethers.Contract(mieroNFTAddress, mieroNFT.abi, signer);

            const mintPrice = ethers.parseEther("0.005");
            const totalCost = mintPrice * BigInt(mintAmount);

            const preMintState = {
                mintAmount,
                mintPrice: ethers.formatEther(mintPrice),
                totalCost: ethers.formatEther(totalCost),
                isEnabled: await contract.isPublicMintEnabled(),
                currentSupply: await contract.totalSupply(),
                maxSupply: await contract.maxSupply()
            };
            console.log("Pre-mint checks:", preMintState);

            const tx = await contract.mint(1, {value: ethers.parseEther("0.005") });

            updateMessage("Transaction submitted! Waiting for confirmation...");
            console.log('Transaction:', {
                hash: tx.hash,
                data: tx.data,
                value: ethers.formatEther(tx.value)
            });

            const receipt = await tx.wait();

            if (receipt.status === 1) {
                updateMessage(`Successfully minted ${mintAmount} token(s)!`);
            } else {
                throw new Error("Transaction failed");
            }

        } catch (err) {
            console.error('Minting error:', err);

            let errorMessage = "An error occurred while minting.";

            if (err.message.includes("insufficient funds")) {
                errorMessage = `Insufficient funds. Required: ${ethers.formatEther(totalCost)} ETH`;
            } else if (err.message.includes("minting not enabled")) {
                errorMessage = "Minting is not enabled";
            } else if (err.message.includes("sold out")) {
                errorMessage = "Sold out";
            } else if(err.message.includes("user rejected transaction")){
                errorMessage = "User rejected transaction";
            }
            else if(err.message.includes("execution reverted")){
                errorMessage = "Execution reverted. Please check if minting is enabled and you have sufficient funds."
            }

            updateMessage(errorMessage);
            console.log('Full error details:', {
                message: err.message,
                data: err.data,
                transaction: err.transaction,
                receipt: err.receipt
            });
        } finally {
            setIsMinting(false);
        }
    } else {
        updateMessage("Please install MetaMask!");
    }
}

  const handleDecrement = () => {
    if (mintAmount <= 1) return;
    setMintAmount(mintAmount - 1);
  };

  const handleIncrement = () => {
    if (mintAmount >= 5) return;
    setMintAmount(mintAmount + 1);
  };

  const renderMintSection = () => {
    // console.log("Rendering mint section with state:", {
    //   isAppConnected,
    //   accountsLength: accounts?.length,
    //   forceUpdate
    // });

    const showMintControls = isAppConnected && accounts && accounts.length > 0;

    return (
      <div key={forceUpdate}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', paddingBottom: '150px' }}>
          <div style={{ width: '520px', textAlign: 'center' }}>
            <div>
              <h1 style={{ fontSize: '48px', textShadow: '0 5px #000000' }}>miero</h1>
            </div>

            {/* Minting Status Box */}
            {isMinting && (
              <div style={{
                backgroundColor: 'rgba(0, 143, 212, 0.1)',
                border: '2px solid #008fd4',
                borderRadius: '8px',
                padding: '20px',
                margin: '20px auto',
                maxWidth: '400px',
                color: '#008fd4',
                boxShadow: '0 0 10px rgba(0, 143, 212, 0.2)'
              }}>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Minting in Progress</span>
                </div>
                <div style={{ fontSize: '14px' }}>
                  {message}
                </div>
              </div>
            )}

            {/* Show error/success messages when not minting */}
            {!isMinting && message && (
              <div style={{
                backgroundColor: message.includes("Success") ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                border: `2px solid ${message.includes("Success") ? '#00ff00' : '#ff0000'}`,
                borderRadius: '8px',
                padding: '20px',
                margin: '20px auto',
                maxWidth: '400px',
                color: message.includes("Success") ? '#00ff00' : '#ff0000'
              }}>
                {message}
              </div>
            )}

            {showMintControls ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '10px 0' }}>
                  <button
                    style={{
                      backgroundColor: '#008fd4',
                      borderRadius: '5px',
                      boxShadow: '0px 2px 2px 1px #0F0F0F',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '15px',
                      margin: '0 10px',
                      fontFamily: 'inherit',
                    }}
                    onClick={handleDecrement}
                  >
                    -
                  </button>

                  <input
                    style={{
                      fontFamily: 'inherit',
                      width: '100px',
                      height: '40px',
                      textAlign: 'center',
                      paddingLeft: '19px',
                      marginTop: '10px',
                    }}
                    readOnly
                    type="number"
                    value={mintAmount}
                  />

                  <button
                    style={{
                      backgroundColor: '#008fd4',
                      borderRadius: '5px',
                      boxShadow: '0px 2px 2px 1px #0F0F0F',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '15px',
                      margin: '0 10px',
                      fontFamily: 'inherit',
                    }}
                    onClick={handleIncrement}
                  >
                    +
                  </button>
                </div>

                <button
                  style={{
                    backgroundColor: '#008fd4',
                    borderRadius: '5px',
                    boxShadow: '0px 2px 2px 1px #0F0F0F',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '15px',
                    margin: '10px',
                    fontFamily: 'inherit',
                  }}
                  onClick={handleMint}
                >
                  Mint Now
                </button>
              </div>
            ) : (
              <div>
                <p
                  style={{
                    marginTop: '-10px',
                    fontSize: '20px',
                    letterSpacing: '5.5%',
                    fontFamily: 'VT323',
                    textShadow: '0 3px #000000',
                    color: '#008fd4',
                  }}
                >
                  Connect your wallet to mint.<br />
                
                  Must be NFT holder to participate in airdrops
                </p>

                {message && (
                  <div style={{
                    padding: '10px',
                    margin: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    borderRadius: '5px',
                    color: '#008fd4'
                  }}>
                    {message}
                  </div>
                )}

                {isMobile && (
                  <div style={{
                    padding: '10px',
                    margin: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    borderRadius: '5px',
                    color: '#008fd4'
                  }}>
                    Please connect using MetaMask mobile app
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Router>
      <div className="overlay" key={forceUpdate}>
        <div className="App">
        
          <NavBar
            accounts={accounts}
            connectWallet={connectWallet}
            disconnectWallet={disconnectWallet}
            formatWalletAddress={formatWalletAddress}
          />
          <Routes>
            <Route path="/tokenomics" element={<Tokenomics />} />
            <Route path="/" element={renderMintSection()} />
          </Routes>
          <div className="moving-background"></div>
        </div>
      </div>
    </Router>
  );
}

export default App;
